/**
 * Authentication Store (Pinia)
 * Manages authentication state, user data, and authentication actions
 */

import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import {
  authService,
  type User,
  type RegisterData,
  AuthError,
  type Group,
  type UserPreferences,
  type UserActiveGroup,
  type OnboardingUpdate,
} from '../services/authService'
import { tokenManager } from '../utils/tokenManager'
import apolloClient from '../graphql/apolloClient'

export interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const groups = ref<Group[] | null>(null)
  const activeGroup = ref<UserActiveGroup | null>(null)
  const preferences = ref<UserPreferences | null>(null)

  // Prevents initializeAuth from running more than once per session
  const isInitialized = ref(false)
  let initializingPromise: Promise<void> | null = null

  // Getters
  const isAuthenticated = computed(() => {
    return !!(user.value && accessToken.value)
  })

  const currentUser = computed(() => user.value)

  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  const setAuthData = (userData: User, access: string, refresh: string) => {
    user.value = userData
    accessToken.value = access
    refreshToken.value = refresh
  }

  const clearAuthData = () => {
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    isInitialized.value = false // allow re-initialization after logout
  }

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      const response = await authService.login(email, password)

      // Store tokens and user data securely
      await tokenManager.setTokens(response.access_token, response.refresh_token, response.user)

      // Update store state
      setAuthData(response.user, response.access_token, response.refresh_token)

      // Clear Apollo Client cache to force re-authentication
      // This ensures that subsequent GraphQL requests use the new token
      await apolloClient.resetStore()

      // Now that authentication is successful and tokens are set,
      // load additional user data (this will use the new auth token)
      try {
        await loadUserData()
      } catch (dataErr) {
        // Log error but don't fail the login if additional data fails to load
        console.error('Failed to load additional user data:', dataErr)
      }
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isUnauthorized || err.isValidationError) {
          setError('Invalid email or password. Please check your credentials.')
        } else if (err.isNetworkError) {
          setError('Network error. Please check your connection and try again.')
        } else {
          setError(err.message || 'Login failed. Please try again.')
        }
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData: RegisterData): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      await authService.register(userData)
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isValidationError) {
          if (err.data?.email) {
            setError('This email is already registered. Please use a different email or try logging in.')
          } else {
            setError('Please check your information and try again.')
          }
        } else if (err.isNetworkError) {
          setError('Network error. Please check your connection and try again.')
        } else {
          setError(err.message || 'Registration failed. Please try again.')
        }
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      // Call logout endpoint (best effort - don't fail if it errors)
      try {
        await authService.logout()
      } catch (err) {
        console.warn('Logout API call failed, but continuing with local cleanup:', err)
      }

      // Clear tokens from storage
      tokenManager.clearTokens()

      // Clear store state
      clearAuthData()

      // Clear additional user data
      groups.value = null
      activeGroup.value = null
      preferences.value = null

      // Clear Apollo Client cache
      await apolloClient.resetStore()
    } catch (err) {
      // Even if logout fails, clear local data
      tokenManager.clearTokens()
      clearAuthData()

      // Clear additional user data
      groups.value = null
      activeGroup.value = null
      preferences.value = null

      // Clear Apollo Client cache
      await apolloClient.resetStore()

      if (err instanceof AuthError) {
        setError(err.message || 'Logout failed, but you have been signed out locally.')
      } else {
        setError('An error occurred during logout, but you have been signed out locally.')
      }
    } finally {
      setLoading(false)
    }
  }

  const refreshAccessToken = async (): Promise<void> => {
    try {
      const currentRefreshToken = await tokenManager.getRefreshToken()

      if (!currentRefreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await authService.refreshToken(currentRefreshToken)

      // Store new tokens
      await tokenManager.setTokens(response.access_token, response.refresh_token)

      // Update store state
      accessToken.value = response.access_token
      refreshToken.value = response.refresh_token

      // Clear Apollo Client cache to use new token
      await apolloClient.resetStore()
    } catch (err) {
      // If refresh fails, clear all auth data and force re-login
      tokenManager.clearTokens()
      clearAuthData()

      if (err instanceof AuthError) {
        setError('Your session has expired. Please log in again.')
      } else {
        setError('Session refresh failed. Please log in again.')
      }
      throw err
    }
  }

  const initializeAuth = async (): Promise<void> => {
    // Already done — skip entirely
    if (isInitialized.value) return
    // In-flight — wait for the running call instead of making a second one
    if (initializingPromise) return initializingPromise
    initializingPromise = _doInitializeAuth()
    try {
      await initializingPromise
    } finally {
      initializingPromise = null
    }
  }

  const _doInitializeAuth = async (): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      // Try to get stored tokens
      const storedAccessToken = await tokenManager.getAccessToken()
      const storedRefreshToken = await tokenManager.getRefreshToken()

      if (!storedAccessToken || !storedRefreshToken) {
        return
      }

      // Check if access token is expired
      const isExpired = tokenManager.isTokenExpired(storedAccessToken)

      if (isExpired) {
        // Try to refresh the token
        try {
          await refreshAccessToken()
        } catch (err) {
          // Refresh failed, user needs to log in
          return
        }
      } else {
        // Access token is still valid, update store state
        accessToken.value = storedAccessToken
        refreshToken.value = storedRefreshToken

        // Try to get stored user data first
        const storedUserData = await tokenManager.getUserData()
        if (storedUserData) {
          user.value = storedUserData
        } else {
          // This is unexpected - we should have user data stored after login
          // Clear tokens and force re-login
          tokenManager.clearTokens()
          clearAuthData()
          return
        }
      }

      // Load additional user data (groups, preferences, active group)
      // if authentication was successful
      if (user.value && accessToken.value) {
        try {
          await loadUserData()
        } catch (dataErr) {
          // Log error but don't fail initialization if additional data fails to load
          console.error('Failed to load additional user data:', dataErr)
        }
      }
    } catch (err) {
      console.error('Auth initialization failed:', err)
      // Clear potentially corrupted data
      tokenManager.clearTokens()
      clearAuthData()
    } finally {
      isInitialized.value = true
      setLoading(false)
    }
  }

  // User Profile Management Actions
  const getUserProfile = async (): Promise<User | null> => {
    try {
      setLoading(true)
      clearError()

      const userProfile = await authService.getUserProfile()

      // Update user data in store and storage
      user.value = userProfile
      await tokenManager.setUserData(userProfile)

      return userProfile
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message || 'Failed to get user profile.')
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateUserProfile = async (profileData: {
    first_name: string
    last_name: string
    username?: string
    email?: string
  }): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      const updatedUser = await authService.updateUserProfile(profileData)

      // Update user data in store and storage
      user.value = updatedUser
      await tokenManager.setUserData(updatedUser)
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isValidationError) {
          setError('Please check your information and try again.')
        } else {
          setError(err.message || 'Failed to update profile.')
        }
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const changePassword = async (passwordData: { current_password: string; new_password: string }): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      await authService.changePassword(passwordData)

      // Password change revokes all refresh tokens, so we need to re-login
      // Clear current session and redirect to login
      tokenManager.clearTokens()
      clearAuthData()
      await apolloClient.resetStore()
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isUnauthorized) {
          setError('Current password is incorrect.')
        } else if (err.isValidationError) {
          setError('Please check your passwords and try again.')
        } else {
          setError(err.message || 'Failed to change password.')
        }
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteAccount = async (password: string): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      await authService.deleteAccount({ password })

      // Clear all data after account deletion
      tokenManager.clearTokens()
      clearAuthData()
      await apolloClient.resetStore()
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isUnauthorized) {
          setError('Password is incorrect.')
        } else {
          setError(err.message || 'Failed to delete account.')
        }
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Two-Factor Authentication Actions
  const get2FAStatus = async (): Promise<{ enabled: boolean } | null> => {
    try {
      const status = await authService.get2FAStatus()
      return status
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message || 'Failed to get 2FA status.')
      } else {
        setError('An unexpected error occurred.')
      }
      return null
    }
  }

  const enable2FA = async (): Promise<{
    secret: string
    qr_code_url: string
    qr_code_image: string
    backup_codes: string[]
  } | null> => {
    try {
      setLoading(true)
      clearError()

      const setupResponse = await authService.enable2FA()
      return setupResponse
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message || 'Failed to enable 2FA.')
      } else {
        setError('An unexpected error occurred.')
      }
      return null
    } finally {
      setLoading(false)
    }
  }

  const verify2FA = async (code: string): Promise<boolean> => {
    try {
      setLoading(true)
      clearError()

      await authService.verify2FA({ code })

      // Update user's 2FA status
      if (user.value) {
        user.value.two_factor_enabled = true
        await tokenManager.setUserData(user.value)
      }

      return true
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isValidationError) {
          setError('Invalid verification code. Please try again.')
        } else {
          setError(err.message || 'Failed to verify 2FA.')
        }
      } else {
        setError('An unexpected error occurred.')
      }
      return false
    } finally {
      setLoading(false)
    }
  }

  const disable2FA = async (password: string, code: string): Promise<boolean> => {
    try {
      setLoading(true)
      clearError()

      await authService.disable2FA({ password, code })

      // Update user's 2FA status
      if (user.value) {
        user.value.two_factor_enabled = false
        await tokenManager.setUserData(user.value)
      }

      return true
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isUnauthorized) {
          setError('Password is incorrect.')
        } else if (err.isValidationError) {
          setError('Invalid verification code or password. Please try again.')
        } else {
          setError(err.message || 'Failed to disable 2FA.')
        }
      } else {
        setError('An unexpected error occurred.')
      }
      return false
    } finally {
      setLoading(false)
    }
  }

  // Password Reset Actions
  const initiatePasswordReset = async (email: string): Promise<{ requires_2fa: boolean }> => {
    try {
      setLoading(true)
      clearError()
      const response = await authService.initiatePasswordReset(email)
      return { requires_2fa: response.requires_2fa }
    } catch (err) {
      // Don't reveal whether the account exists
      throw err
    } finally {
      setLoading(false)
    }
  }

  const verifyPasswordReset = async (
    email: string,
    resetToken: string,
    totpCode?: string,
  ): Promise<{ temporary_token: string }> => {
    try {
      setLoading(true)
      const response = await authService.verifyPasswordReset(email, resetToken, totpCode)
      return { temporary_token: response.temporary_token }
    } catch (err) {
      // Rethrow without setting store error — component handles display
      throw err
    } finally {
      setLoading(false)
    }
  }

  const completePasswordReset = async (email: string, temporaryToken: string, newPassword: string): Promise<void> => {
    try {
      setLoading(true)
      clearError()
      await authService.completePasswordReset(email, temporaryToken, newPassword)
    } catch (err) {
      if (err instanceof AuthError) {
        if (err.isValidationError) {
          setError('Password does not meet requirements.')
        } else if (err.isUnauthorized) {
          setError('Reset token is invalid or expired. Please start over.')
        } else {
          setError(err.message || 'Failed to reset password.')
        }
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Group Management Actions (example of additional functionality)
  const getUserGroups = async (): Promise<Group[] | null> => {
    try {
      setLoading(true)
      clearError()

      const userGroups = await authService.getUserGroups()

      // Update user data in store and storage
      groups.value = userGroups

      return userGroups
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message || 'Failed to get user groups.')
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Preferences
  const getUserPreferences = async (): Promise<UserPreferences | null> => {
    try {
      setLoading(true)
      clearError()

      const userPreferences = await authService.getUserPreferences()

      // Update user data in store and storage
      preferences.value = userPreferences

      return userPreferences
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message || 'Failed to get user preferences.')
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getUserActiveGroup = async (): Promise<UserActiveGroup | null> => {
    try {
      setLoading(true)
      clearError()

      const userActiveGroup = await authService.getUserActiveGroup()

      // Update user data in store and storage
      activeGroup.value = userActiveGroup

      return userActiveGroup
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message || 'Failed to get user active group.')
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  const setUserActiveGroup = async (groupID: number): Promise<UserPreferences | null> => {
    try {
      setLoading(true)
      clearError()

      const userPreferences = await authService.setUserActiveGroup(groupID)

      // Update preferences in store
      preferences.value = userPreferences

      // Also update activeGroup to reflect the change immediately
      if (userPreferences && userPreferences.active_group) {
        activeGroup.value = {
          active_group: userPreferences.active_group,
        }
      }

      return userPreferences
    } catch (err) {
      if (err instanceof AuthError) {
        setError(err.message || 'Failed to set user active group.')
      } else {
        setError('An unexpected error occurred.')
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Persist onboarding progress (current step and/or completion flag).
  const updateOnboarding = async (data: OnboardingUpdate): Promise<UserPreferences | null> => {
    try {
      const userPreferences = await authService.updateOnboarding(data)
      preferences.value = userPreferences
      return userPreferences
    } catch (err) {
      // Onboarding persistence is best-effort — never block the UI on it.
      console.warn('Failed to persist onboarding progress:', err)
      return null
    }
  }

  // Helper function to load user data after authentication
  const loadUserData = async (): Promise<void> => {
    // Load all user data in parallel for better performance
    const promises: Promise<any>[] = []

    // Load active group
    promises.push(
      authService
        .getUserActiveGroup()
        .then((data) => {
          activeGroup.value = data
        })
        .catch((err) => {
          console.warn('Failed to load active group:', err)
        }),
    )

    // Load groups
    promises.push(
      authService
        .getUserGroups()
        .then((data) => {
          groups.value = data
        })
        .catch((err) => {
          console.warn('Failed to load groups:', err)
        }),
    )

    // Load preferences
    promises.push(
      authService
        .getUserPreferences()
        .then((data) => {
          preferences.value = data
        })
        .catch((err) => {
          console.warn('Failed to load preferences:', err)
        }),
    )

    // Wait for all promises to settle
    await Promise.allSettled(promises)
  }

  return {
    // State
    user: readonly(user),
    accessToken: readonly(accessToken),
    refreshToken: readonly(refreshToken),
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Getters
    isAuthenticated,
    currentUser,
    activeGroup: readonly(activeGroup),
    groups: readonly(groups),
    preferences: readonly(preferences),

    // Actions
    login,
    register,
    logout,
    refreshAccessToken,
    initializeAuth,
    clearError,
    setLoading,
    setError,

    // User Profile Management
    getUserProfile,
    updateUserProfile,
    changePassword,
    deleteAccount,

    // Two-Factor Authentication
    get2FAStatus,
    enable2FA,
    verify2FA,
    disable2FA,

    // Password Reset
    initiatePasswordReset,
    verifyPasswordReset,
    completePasswordReset,

    // Group Management
    getUserGroups,

    //Preferences
    getUserPreferences,
    getUserActiveGroup,
    setUserActiveGroup,
    updateOnboarding,

    // Helper
    loadUserData,
  }
})
